import {
  getCooldownRemainingMs,
  getLastResendAtMillis,
  localRetryKey,
  RetryTarget,
  toMillis
} from '../../../../../services/experiences/retryCooldown';
import type { ExperienceProblemData } from '../InstanceProblems';

/**
 * SC-36146 — per-issue "retry queued" state for the issues bar. Pure; the UI
 * passes `nowMillis` and the per-browser marks in.
 */

/** Same visibility rule as the row's Retry button. */
export const isRetryableIssue = (d: ExperienceProblemData): boolean =>
  d.type !== 'MISSING_ACTOR' && (d.type !== 'SENT_ITEM' || !!d.channels.length);

/**
 * The timeline item whose BE resend events (MESSAGE_RESENT / TASK_RESENT) say
 * a retry was queued. Null for issues whose retry the BE doesn't record as an
 * event (automations, supporting actors, integrations…) — those use the
 * per-browser mark instead.
 */
export const getIssueRetryTarget = (d: ExperienceProblemData): RetryTarget | null => {
  const taskTplIdsFor = (taskIds: string[]) =>
    taskIds
      .map((id) => d.instance.data.tasks?.find((t) => t.id === id)?.templateId)
      .filter((id): id is string => !!id);

  if (d.type === 'SENT_ITEM') {
    if (d.message.type === 'EXPERIENCE_MESSAGE' && d.message.scope?.msgId) {
      return { itemType: 'MESSAGE', msgTplId: d.message.scope.msgId };
    }
    if (d.message.type === 'TASK_START') {
      const taskTplIds = taskTplIdsFor(d.message.scope?.taskIds ?? []);
      return taskTplIds.length ? { itemType: 'TASK', taskTplIds } : null;
    }
    return null;
  }
  if (d.type === 'ASSIGNEE_BOUNCE') {
    if (d.itemType === 'MESSAGE') {
      return { itemType: 'MESSAGE', msgTplId: d.itemTplId };
    }
    // TASK bounces store a task INSTANCE id in itemTplId (see Details/index.tsx).
    const taskTplIds = taskTplIdsFor([d.itemTplId]);
    return taskTplIds.length ? { itemType: 'TASK', taskTplIds } : null;
  }
  return null;
};

/** When the failure this row shows happened, if known. */
export const getIssueFailedAtMillis = (d: ExperienceProblemData): number | null => {
  const raw =
    d.type === 'SENT_ITEM'
      ? d.message.at
      : d.type === 'AUTOMATION'
        ? d.denormalized.doneAt
        : (d as { at?: unknown }).at;
  return toMillis(raw) || null;
};

/** Key for the per-browser mark of a retry without a BE event. */
export const getIssueLocalRetryKey = (d: ExperienceProblemData): string =>
  localRetryKey(d.instance.data.orgId, d.instance.id, `${d.type}:${d.id}`);

/**
 * Milliseconds until this row may be retried again (0 = retry allowed).
 */
export const getIssueRetryCooldownMs = (
  d: ExperienceProblemData,
  nowMillis: number,
  localMarks: Record<string, number>
): number => {
  const target = getIssueRetryTarget(d);
  const queuedAt = target
    ? getLastResendAtMillis(d.instance.data.events, target)
    : localMarks[getIssueLocalRetryKey(d)] ?? null;
  return getCooldownRemainingMs(queuedAt, nowMillis, getIssueFailedAtMillis(d));
};

/** Per-browser marks that belong to the given instances (for the header banner). */
export const countLocalMarksForInstances = (
  localMarks: Record<string, number>,
  instanceIds: Set<string>
): number =>
  Object.keys(localMarks).filter((k) => instanceIds.has(k.split(':')[1] ?? '')).length;
